import { useEffect, useState, useCallback } from 'react';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useAuth } from '@/core/auth/auth';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { Search, UserCheck, UserX, Copy, Check, Mail } from 'lucide-react';

interface WaitlistEntry {
  id: string;
  email: string;
  full_name: string;
  professional_role: string;
  institution: string | null;
  reason: string | null;
  status: string;
  invite_code: string | null;
  invited_at: string | null;
  registered_at: string | null;
  registered_user_id: string | null;
  created_at: string;
}

const statusColors: Record<string, string> = {
  pending: 'bg-amber-500/10 text-amber-500 border-amber-500/20',
  invited: 'bg-green-500/10 text-green-500 border-green-500/20',
  rejected: 'bg-red-500/10 text-red-500 border-red-500/20',
  registered: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
};

export default function WaitlistAdmin() {
  const { session } = useAuth();
  const [entries, setEntries] = useState<WaitlistEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [searchTerm, setSearchTerm] = useState('');
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    action: 'approve' | 'reject';
    entry: WaitlistEntry;
  } | null>(null);
  const [inviteDialog, setInviteDialog] = useState<{
    open: boolean;
    code: string;
    email: string;
  } | null>(null);

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('waitlist')
        .select('*')
        .order('created_at', { ascending: false });

      if (statusFilter && statusFilter !== 'all') {
        query = query.eq('status', statusFilter);
      }

      if (searchTerm) {
        query = query.or(`email.ilike.%${searchTerm}%,full_name.ilike.%${searchTerm}%`);
      }

      const { data, error } = await query;

      if (error) {
        console.error('[WaitlistAdmin] Fetch error:', error);
        toast.error('Failed to load waitlist entries');
        return;
      }

      setEntries((data as WaitlistEntry[]) || []);
    } catch (err) {
      console.error('[WaitlistAdmin] Unexpected error:', err);
      toast.error('Failed to load waitlist entries');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, searchTerm]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  const handleAction = async () => {
    if (!confirmDialog || !session?.access_token) return;

    try {
      const { data, error } = await supabase.functions.invoke('waitlist-approve', {
        headers: { Authorization: `Bearer ${session.access_token}` },
        body: {
          waitlist_id: confirmDialog.entry.id,
          action: confirmDialog.action,
        },
      });

      if (error) {
        toast.error(`Failed to ${confirmDialog.action} entry`);
        return;
      }

      if (confirmDialog.action === 'approve' && data?.invite_code) {
        setInviteDialog({
          open: true,
          code: data.invite_code,
          email: confirmDialog.entry.email,
        });
        if (data?.email_sent === false) {
          toast.warning('Invite code created, but email was not sent automatically');
        }
      }

      toast.success(
        confirmDialog.action === 'approve'
          ? `Invite code generated for ${confirmDialog.entry.email}`
          : `Rejected ${confirmDialog.entry.email}`
      );

      fetchEntries();
    } catch {
      toast.error('Action failed');
    } finally {
      setConfirmDialog(null);
    }
  };

  const handleCopyCode = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(code);
      toast.success('Invite code copied');
      setTimeout(() => setCopiedCode(null), 2000);
    } catch {
      toast.error('Failed to copy');
    }
  };

  const handleCopySignupLink = async (code: string) => {
    try {
      const link = `${window.location.origin}/signup?code=${code}`;
      await navigator.clipboard.writeText(link);
      toast.success('Signup link copied');
    } catch {
      toast.error('Failed to copy');
    }
  };

  const pendingCount = entries.filter(e => e.status === 'pending').length;
  const invitedCount = entries.filter(e => e.status === 'invited').length;
  const registeredCount = entries.filter(e => e.status === 'registered').length;

  return (
    <AdminLayout
      title="Waitlist Management"
      description="Review and approve beta access requests"
    >
      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold">{entries.length}</div>
            <div className="text-sm text-muted-foreground">Total Entries</div>
          </CardContent>
        </Card>
        <Card className={pendingCount > 0 ? 'border-amber-500/50' : ''}>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-amber-500">{pendingCount}</div>
            <div className="text-sm text-muted-foreground">Pending Review</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-green-500">{invitedCount}</div>
            <div className="text-sm text-muted-foreground">Invited</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-blue-500">{registeredCount}</div>
            <div className="text-sm text-muted-foreground">Registered</div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row gap-4 justify-between">
            <CardTitle>Waitlist Entries</CardTitle>
            <div className="flex gap-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && fetchEntries()}
                  className="pl-10 w-64"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Filter" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="invited">Invited</SelectItem>
                  <SelectItem value="registered">Registered</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={fetchEntries} variant="outline" size="sm">
                Refresh
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Loading...</div>
          ) : entries.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">No entries found</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Institution</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Invite Code</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.map((entry) => (
                  <TableRow key={entry.id}>
                    <TableCell className="font-medium">{entry.full_name}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{entry.email}</TableCell>
                    <TableCell>
                      <Badge variant="secondary" className="capitalize">
                        {entry.professional_role}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {entry.institution || '-'}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={statusColors[entry.status] || ''}>
                        {entry.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {entry.invite_code ? (
                        <div className="flex items-center gap-1">
                          <code className="text-xs bg-muted px-2 py-0.5 rounded font-mono">
                            {entry.invite_code}
                          </code>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0"
                            onClick={() => handleCopyCode(entry.invite_code!)}
                          >
                            {copiedCode === entry.invite_code ? (
                              <Check className="h-3 w-3 text-green-500" />
                            ) : (
                              <Copy className="h-3 w-3 text-muted-foreground" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0"
                            onClick={() => handleCopySignupLink(entry.invite_code!)}
                            title="Copy signup link"
                          >
                            <Mail className="h-3 w-3 text-muted-foreground" />
                          </Button>
                        </div>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {format(new Date(entry.created_at), 'MMM d, yyyy')}
                    </TableCell>
                    <TableCell className="text-right">
                      {entry.status === 'pending' && (
                        <div className="flex gap-2 justify-end">
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-green-500 hover:text-green-600"
                            onClick={() => setConfirmDialog({
                              open: true,
                              action: 'approve',
                              entry,
                            })}
                          >
                            <UserCheck className="h-4 w-4 mr-1" />
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-500 hover:text-red-600"
                            onClick={() => setConfirmDialog({
                              open: true,
                              action: 'reject',
                              entry,
                            })}
                          >
                            <UserX className="h-4 w-4 mr-1" />
                            Reject
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Confirm Dialog */}
      <AlertDialog open={!!confirmDialog} onOpenChange={() => setConfirmDialog(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmDialog?.action === 'approve' ? 'Approve Application' : 'Reject Application'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDialog?.action === 'approve'
                ? `Generate an invite code for ${confirmDialog?.entry.full_name} (${confirmDialog?.entry.email})?`
                : `Reject the application from ${confirmDialog?.entry.full_name} (${confirmDialog?.entry.email})?`
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleAction}
              className={confirmDialog?.action === 'reject' ? 'bg-destructive hover:bg-destructive/90' : ''}
            >
              {confirmDialog?.action === 'approve' ? 'Approve & Generate Code' : 'Reject'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Invite Code Dialog */}
      <Dialog open={!!inviteDialog} onOpenChange={() => setInviteDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invite Code Generated</DialogTitle>
            <DialogDescription>
              Send this code to {inviteDialog?.email}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center justify-center gap-2 p-4 bg-muted rounded-lg">
              <code className="text-2xl font-mono font-bold tracking-widest">
                {inviteDialog?.code}
              </code>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => inviteDialog && handleCopyCode(inviteDialog.code)}
              >
                {copiedCode === inviteDialog?.code ? (
                  <Check className="h-4 w-4 text-green-500" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </Button>
            </div>
            <Button
              className="w-full"
              onClick={() => inviteDialog && handleCopySignupLink(inviteDialog.code)}
            >
              <Mail className="h-4 w-4 mr-2" />
              Copy Signup Link
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
